import { get, find, isEmpty } from 'lodash';
import { type View } from 'common/types/view';
import { type IRowNode } from 'ag-grid-community';
import { type Configuration } from 'common/visualizations/vif';
import { type TableActionsConfigs, type TableActionsContextField } from '../TableActionsButton/types';

interface DescriptorMetadata {
  name: string;
  value: string;
}

export interface ApplicationDescriptor {
  id: string;
  url: string;
  metadata: DescriptorMetadata[];
}

export interface ProcessDescriptor {
  id: string;
  name: string;
  url: string;
  metadata: DescriptorMetadata[];
}

export const defaultTableActionsContext: TableActionsConfigs = {
  mappedColumns: null,
  mediaType: '',
  domain: '',
  apiVersion: 'v1',
};

export const defaultLinkedAction = {
  selectedExistingColumn: '',
  selectedActionForExistingColumn: '',
};

export const getTableActionsContext = (viewMetadata: View, vifConfig: Configuration): TableActionsConfigs => {
  const mappedColumns = vifConfig.contextFieldMappings;

  if (isEmpty(viewMetadata)) {
    return defaultTableActionsContext;
  }
  const { metadata } = viewMetadata;

  // Prefer VIF configuration, fallback to rowIdentifierColumnId

  const munisContext = find(get(metadata, 'custom_fields', {}), (__, fieldKey) => {
    return fieldKey === 'MUNIS Context Metadata';
  });
  const { Domain: domain, 'Process Discovery API Version': apiVersion } = munisContext || {};

  return {
    mappedColumns: mappedColumns ?? null,
    mediaType: vifConfig?.actionsMediaType,
    domain,
    apiVersion,
  };
};

export const isTableActionConfigured = (configuration: Configuration | null | undefined): boolean =>
  Boolean(configuration?.showProcessDiscoveryAction || configuration?.showExistingActionColumn);

export type ResolvedColumnValue =
  | { status: 'found'; value: string | number }
  | { status: 'ambiguous' }
  | { status: 'missing' };

/**
 * Row-data key under which the server-side "uniform value or null" aggregate
 * for a column lands on grouped parent rows. Namespaced so it can't collide
 * with the column's own field name.
 */
export const tableActionValueKey = (fieldName: string) => `__tableActionValue__${fieldName}`;

/**
 * Resolves a column value for an ag-grid row, including grouped parent rows.
 *
 * Resolution order:
 *  1. The row's own `data[col]` — covers leaf rows that carry every value,
 *     and parent rows for the grouping column at their level.
 *  2. For group rows missing a direct value, the server-side aggregate
 *     under {@link tableActionValueKey}:
 *       - key present, non-null  → children agreed → found
 *       - key present, null      → children disagreed → ambiguous
 *       - key absent             → not loaded / not configured → missing
 *  3. For leaf rows missing a direct value, walk up `node.parent.data`.
 *     ag-grid's grouped SELECT often excludes the group-key column from
 *     the leaf level (the parent already has it), so a required field
 *     mapped to a grouping column lives one or more levels above.
 *  4. Still nothing → `found ''` so the link renders (preserving the
 *     pre-grouping render-anyway behavior).
 */
export const resolveColumnValueForRow = (
  node: IRowNode | null | undefined,
  data: Record<string, unknown> | null | undefined,
  columnFieldName: string,
): ResolvedColumnValue => {
  if (!columnFieldName) {
    return { status: 'missing' };
  }

  const directValue = data?.[columnFieldName];
  if (directValue != null && directValue !== '') {
    return { status: 'found', value: directValue as string | number };
  }

  if (node?.group) {
    const actionKey = tableActionValueKey(columnFieldName);
    if (data && actionKey in data) {
      const actionValue = data[actionKey];
      return actionValue == null
        ? { status: 'ambiguous' }
        : { status: 'found', value: actionValue as string | number };
    }
    return { status: 'missing' };
  }

  let ancestor = node?.parent;
  while (ancestor) {
    const ancestorValue = (ancestor.data as Record<string, unknown> | null | undefined)?.[columnFieldName];
    if (ancestorValue != null && ancestorValue !== '') {
      return { status: 'found', value: ancestorValue as string | number };
    }
    ancestor = ancestor.parent;
  }

  return { status: 'found', value: '' };
};

export interface ResolvedMappedColumns {
  columns: TableActionsContextField[];
  ambiguous: boolean;
  missing: boolean;
}

/**
 * Resolves every mapped column for a row, flagging when any required value
 * is ambiguous (children disagree) or missing. Callers use the flags to
 * decide whether to render the action affordance.
 */
export const resolveMappedColumnsForRow = (
  node: IRowNode | null | undefined,
  data: Record<string, unknown> | null | undefined,
  mappedColumns: TableActionsContextField[],
): ResolvedMappedColumns => {
  let ambiguous = false;
  let missing = false;

  const columns = mappedColumns.map((contextField) => {
    const resolved = resolveColumnValueForRow(node, data, contextField.selectedColumn);
    if (resolved.status === 'ambiguous') {
      ambiguous = true;
    }
    if (resolved.status === 'missing') {
      missing = true;
    }
    return {
      ...contextField,
      value: resolved.status === 'found' ? resolved.value : undefined,
    };
  });

  return { columns, ambiguous, missing };
};

export const hasEmbeddedExecutionMethod = (applicationDescriptor: ApplicationDescriptor) => {
  const metadata = get(applicationDescriptor, 'metadata', []);
  const embeddedMetadata = find(metadata, { name: 'executionMethod', value: 'Embedded' });
  return !isEmpty(embeddedMetadata);
};
